import type { IntegrationProviderId } from '../types/integration'

export interface ProviderHealthAudit {
  providerId: IntegrationProviderId
  name: string
  activeCount: number
  degradedCount: number
  failedCount: number
  totalCount: number
  healthTone: 'good' | 'warn' | 'danger' | 'neutral'
  healthLabel: 'Operational' | 'Degraded' | 'Attention needed' | 'Not configured'
  lastSyncAt: string | null
}

export interface CrossWorkspaceIntegrationAudit {
  totalConnections: number
  activeCount: number
  degradedCount: number
  failedCount: number
  byProvider: Record<IntegrationProviderId, ProviderHealthAudit>
  inboundStreamStatus: 'COMING_SOON'
  inboundStreamMessage: string
}

export interface RawConnectionRecord {
  id?: string
  workspaceId?: string
  providerId: string
  status: string
  lastSync?: { toDate?: () => Date } | Date | string | null
  errorSummary?: string | null
}

const PROVIDER_NAMES: Record<IntegrationProviderId, string> = {
  NOTION: 'Notion',
  JIRA: 'Jira',
  GOOGLE_CALENDAR: 'Google Calendar',
  GOOGLE_SHEETS: 'Google Sheets',
  G_DRIVE: 'Google Drive',
  GENERIC_WEBHOOK: 'Generic Webhook',
  SLACK: 'Slack',
  FIGMA: 'Figma',
  ZOOM: 'Zoom',
  DROPBOX: 'Dropbox',
  GITHUB: 'GitHub',
  STAYS_PMS: 'Hotel PMS',
}

const ALL_AUDITED_PROVIDERS: IntegrationProviderId[] = [
  'NOTION',
  'JIRA',
  'GOOGLE_CALENDAR',
  'GOOGLE_SHEETS',
  'G_DRIVE',
  'GENERIC_WEBHOOK',
  'SLACK',
  'FIGMA',
  'ZOOM',
  'DROPBOX',
  'GITHUB',
  'STAYS_PMS',
]

export const aggregateCrossWorkspaceConnectionHealth = (
  connections: RawConnectionRecord[],
): CrossWorkspaceIntegrationAudit => {
  let totalConnections = 0
  let activeCount = 0
  let degradedCount = 0
  let failedCount = 0

  const providerBuckets: Record<
    IntegrationProviderId,
    { active: number; degraded: number; failed: number; latestSync: Date | null }
  > = {
    NOTION: { active: 0, degraded: 0, failed: 0, latestSync: null },
    JIRA: { active: 0, degraded: 0, failed: 0, latestSync: null },
    GOOGLE_CALENDAR: { active: 0, degraded: 0, failed: 0, latestSync: null },
    GOOGLE_SHEETS: { active: 0, degraded: 0, failed: 0, latestSync: null },
    G_DRIVE: { active: 0, degraded: 0, failed: 0, latestSync: null },
    GENERIC_WEBHOOK: { active: 0, degraded: 0, failed: 0, latestSync: null },
    SLACK: { active: 0, degraded: 0, failed: 0, latestSync: null },
    FIGMA: { active: 0, degraded: 0, failed: 0, latestSync: null },
    ZOOM: { active: 0, degraded: 0, failed: 0, latestSync: null },
    DROPBOX: { active: 0, degraded: 0, failed: 0, latestSync: null },
    GITHUB: { active: 0, degraded: 0, failed: 0, latestSync: null },
    STAYS_PMS: { active: 0, degraded: 0, failed: 0, latestSync: null },
  }

  for (const conn of connections) {
    const providerKey = conn.providerId as IntegrationProviderId
    if (!ALL_AUDITED_PROVIDERS.includes(providerKey)) continue

    totalConnections += 1
    const status = (conn.status || '').toUpperCase()

    let parsedDate: Date | null = null
    if (conn.lastSync) {
      if (typeof (conn.lastSync as { toDate?: () => Date }).toDate === 'function') {
        parsedDate = (conn.lastSync as { toDate: () => Date }).toDate()
      } else if (conn.lastSync instanceof Date) {
        parsedDate = conn.lastSync
      } else if (typeof conn.lastSync === 'string') {
        const d = new Date(conn.lastSync)
        if (!isNaN(d.getTime())) parsedDate = d
      }
    }

    if (parsedDate && (!providerBuckets[providerKey].latestSync || parsedDate > providerBuckets[providerKey].latestSync!)) {
      providerBuckets[providerKey].latestSync = parsedDate
    }

    if (status === 'ACTIVE' || status === 'CONNECTED') {
      activeCount += 1
      providerBuckets[providerKey].active += 1
    } else if (status === 'PENDING' || status === 'EXPIRED' || status === 'RATE_LIMITED') {
      degradedCount += 1
      providerBuckets[providerKey].degraded += 1
    } else {
      failedCount += 1
      providerBuckets[providerKey].failed += 1
    }
  }

  const byProvider = {} as Record<IntegrationProviderId, ProviderHealthAudit>

  for (const providerId of ALL_AUDITED_PROVIDERS) {
    const bucket = providerBuckets[providerId]
    const total = bucket.active + bucket.degraded + bucket.failed

    let healthTone: 'good' | 'warn' | 'danger' | 'neutral' = 'neutral'
    let healthLabel: 'Operational' | 'Degraded' | 'Attention needed' | 'Not configured' = 'Not configured'

    if (total > 0) {
      if (bucket.failed > 0) {
        healthTone = 'danger'
        healthLabel = 'Attention needed'
      } else if (bucket.degraded > 0) {
        healthTone = 'warn'
        healthLabel = 'Degraded'
      } else {
        healthTone = 'good'
        healthLabel = 'Operational'
      }
    }

    byProvider[providerId] = {
      providerId,
      name: PROVIDER_NAMES[providerId] ?? providerId,
      activeCount: bucket.active,
      degradedCount: bucket.degraded,
      failedCount: bucket.failed,
      totalCount: total,
      healthTone,
      healthLabel,
      lastSyncAt: bucket.latestSync ? bucket.latestSync.toISOString() : null,
    }
  }

  return {
    totalConnections,
    activeCount,
    degradedCount,
    failedCount,
    byProvider,
    inboundStreamStatus: 'COMING_SOON',
    inboundStreamMessage: 'Coming soon — cross-workspace inbound event stream in a future release.',
  }
}
